import { useCallback, useEffect, useState } from 'react';
import type { DragEndEvent } from '@dnd-kit/core';
import type { SeatingState } from '@peles/shared';
import { useSeatingActions, useSeatingKeys } from '../../seating/useSeatingActions';
import { resolveSeatingDrop } from '../../seating/dropTargets';
import type { ResponseFilter } from '../../seating/boardModel';

/**
 * The floor plan's Seating mode: who is picked up, whose sheet is open, and
 * what a tap or a drop on the canvas means. Every write goes through the same
 * `useSeatingActions` the seating board uses, so both undo alike.
 *
 * - A tap on a party in the list picks it up; a tap on a table seats it there,
 *   a tap on an empty chair seats it at that chair (a taken chair: at the
 *   table, wherever is free).
 * - With nobody picked up, a tap on a taken chair opens its party's sheet.
 * - Escape puts a picked-up party down; Ctrl+Z undoes.
 */
export function useFloorPlanSeatingMode(
  eventId: string | undefined,
  state: SeatingState | undefined,
  active: boolean,
) {
  const actions = useSeatingActions(eventId ?? '', active ? state : undefined);
  const [selected, setSelected] = useState<string | null>(null);
  const [sheetGuest, setSheetGuest] = useState<string | null>(null);
  const [filter, setFilter] = useState<ResponseFilter>('coming');
  const planId = state?.plan.id;
  const { reset } = actions.history;

  // Another plan, or leaving the mode, starts clean: nothing picked up, and
  // no undo of changes made on a plan no longer on screen.
  useEffect(() => {
    setSelected(null);
    setSheetGuest(null);
    reset();
  }, [planId, active, reset]);

  const seat = useCallback(
    async (guestId: string, tableUid: string | null, seatNumber?: number) => {
      setSelected(null);
      await actions.seat(guestId, tableUid, seatNumber);
    },
    [actions],
  );

  const tapTable = useCallback(
    (tableUid: string) => {
      if (selected) void seat(selected, tableUid);
    },
    [selected, seat],
  );

  const tapSeat = useCallback(
    (tableUid: string, seatNumber: number) => {
      const table = state?.tables.find((t) => t.uid === tableUid);
      const occupant = table?.occupied.find((s) => s.seatNumber === seatNumber);
      if (selected) {
        void seat(selected, tableUid, occupant ? undefined : seatNumber);
      } else if (occupant) {
        setSheetGuest(occupant.guestId);
      }
    },
    [state, selected, seat],
  );

  const onDragEnd = useCallback(
    (event: DragEndEvent) => {
      const drop = resolveSeatingDrop(actions.parties, event.active.id, event.over?.id);
      if (drop) void seat(drop.guestId, drop.tableUid, drop.seatNumber);
    },
    [actions.parties, seat],
  );

  const toggleSelected = useCallback(
    (guestId: string) => setSelected((current) => (current === guestId ? null : guestId)),
    [],
  );
  const putDown = useCallback(() => setSelected(null), []);

  useSeatingKeys({
    enabled: active,
    onUndo: () => void actions.undo(),
    onRedo: () => void actions.redo(),
    onEscape: putDown,
  });

  return {
    active,
    state: active ? state : undefined,
    actions,
    selected,
    toggleSelected,
    sheetGuest,
    setSheetGuest,
    filter,
    setFilter,
    seat,
    tapTable,
    tapSeat,
    onDragEnd,
  };
}

export type FloorPlanSeatingMode = ReturnType<typeof useFloorPlanSeatingMode>;
